import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiFetch } from '../../services/apiClient';
import { motion } from 'motion/react';
import { Volume2, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import { VoicePicker, voiceDisplayName } from '../VoicePicker';
import { VoiceForge } from '../VoiceForge';

// ─── VoiceCard sub-component ───

function VoiceCard({ voice, isClone, isPlaying, onPlay }: { voice: any; isClone?: boolean; isPlaying?: boolean; onPlay: () => void }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl transition-all group ${
      isPlaying ? 'bg-sky-500/10 border border-sky-500/20' : 'bg-white/[0.03] border border-white/[0.04] hover:bg-white/[0.06]'
    }`}>
      <button
        onClick={onPlay}
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${
          isPlaying ? 'bg-sky-500 text-white' : 'bg-white/10 text-white/50 group-hover:text-white'
        }`}
      >
        {isPlaying ? <Pause size={14} /> : <Play size={14} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold text-white/80 truncate">{voiceDisplayName(voice)}</div>
        <div className="text-[10px] text-white/40 uppercase">{voice.language || voice.provider || ''}</div>
      </div>
      {isClone && <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />}
    </div>
  );
}

// ─── SoundPanel component ───

export function SoundPanel({ t, onOpenAvatarStudio }: { t?: any; onOpenAvatarStudio?: () => void }) {
  const { selectedVoiceId, autoSpeakReply, setAutoSpeakReply } = useApp();
  const [designPrompt, setDesignPrompt] = useState('');
  const [designName, setDesignName] = useState('');
  const [designing, setDesigning] = useState(false);
  const [voiceRefresh, setVoiceRefresh] = useState(0);
  const [voices, setVoices] = useState<{ cloned: any[]; premade: any[] }>({ cloned: [], premade: [] });
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    apiFetch('/api/voice/voices', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setVoices({ cloned: d.cloned || [], premade: d.premade || [] }))
      .catch(() => {});
  }, [voiceRefresh]);

  const handlePlay = useCallback(async (voiceId: string, text?: string) => {
    if (playingId === voiceId) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    try {
      const res = await apiFetch('/api/voice/synthesize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voiceId, text: text || '你好，这是我的声音。Hello, this is my voice.' }),
      });
      if (!res.ok) throw new Error('Synthesis failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); };
      await audio.play();
      setPlayingId(voiceId);
    } catch { toast.error('Playback failed'); }
  }, [playingId]);

  const handleDesign = useCallback(async () => {
    if (!designPrompt.trim() || !designName.trim()) return;
    setDesigning(true);
    try {
      const res = await apiFetch('/api/voice/design', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: designPrompt.trim(), name: designName.trim() }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error((await res.json()).error);
      const data = await res.json();
      toast.success(`Voice "${data.name}" created`);
      setDesignPrompt('');
      setDesignName('');
      setVoiceRefresh(n => n + 1);
    } catch (err: any) {
      toast.error(err.message || (t?.voiceDesignFailed || '语音设计失败'));
    } finally {
      setDesigning(false);
    }
  }, [designPrompt, designName, t?.voiceDesignFailed]);

  const voiceIdentitySteps = [
    {
      id: 'design',
      label: t?.voiceFlowDesign || '设计',
      desc: t?.voiceFlowDesignDesc || '根据描述生成语音',
      active: designing,
      done: voices.cloned.length + voices.premade.length > 0,
    },
    {
      id: 'clone',
      label: t?.voiceFlowClone || '克隆',
      desc: t?.voiceFlowCloneDesc || '录制或上传真实样本',
      active: false,
      done: voices.cloned.length > 0,
    },
    {
      id: 'select',
      label: t?.voiceFlowSelect || '启用',
      desc: t?.voiceFlowSelectDesc || 'Choose 小枫 voice',
      active: false,
      done: Boolean(selectedVoiceId),
    },
    {
      id: 'avatar',
      label: t?.voiceFlowAvatar || '形象',
      desc: t?.voiceFlowAvatarDesc || '声音与外貌匹配',
      active: false,
      done: false,
    },
  ];

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500 h-full flex flex-col">
      <div className="flex items-center gap-3 shrink-0">
        <div className="p-3 bg-gradient-to-br from-sky-500 to-indigo-600 rounded-2xl shadow-lg">
          <Volume2 size={24} className="text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold uppercase tracking-tighter text-white/90">{t?.voiceStudio || '语音工作室'}</h3>
          <p className="text-xs text-white/55 uppercase tracking-widest">{t?.voiceStudioDesc || 'Cloning & 设计'}</p>
        </div>
        <div className="ml-auto">
          <VoicePicker t={t} direction="down" refreshTrigger={voiceRefresh} />
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-4 gap-2 rounded-2xl border border-white/5 bg-white/[0.02] p-2">
        {voiceIdentitySteps.map((step, index) => (
          <button
            key={step.id}
            onClick={step.id === 'avatar' ? onOpenAvatarStudio : undefined}
            disabled={step.id !== 'avatar'}
            className={`group min-w-0 rounded-xl border px-3 py-2 text-left transition-colors ${
              step.done
                ? 'border-emerald-400/20 bg-emerald-400/10'
                : step.active
                  ? 'border-sky-400/30 bg-sky-400/10'
                  : step.id === 'avatar'
                    ? 'border-cyan-400/20 bg-cyan-400/10 hover:bg-cyan-400/20'
                    : 'border-white/5 bg-black/20'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-black ${
                step.done ? 'bg-emerald-300 text-black' : step.active ? 'bg-sky-300 text-black' : 'bg-white/10 text-white/45'
              }`}>
                {step.done ? '✓' : index + 1}
              </span>
              <span className="truncate text-[11px] font-black uppercase tracking-[0.12em] text-white/72">{step.label}</span>
            </div>
            <p className="mt-1 truncate text-[10px] font-semibold text-white/35">{step.desc}</p>
          </button>
        ))}
      </div>

      <div className="flex-1 grid grid-cols-2 gap-4 overflow-hidden">
        <div className="overflow-y-auto scrollbar-hide space-y-4">
          <div className="rounded-2xl bg-white/[0.02] border border-white/5 p-4 space-y-4">
            <h4 className="text-xs font-black uppercase tracking-widest text-white/55">{t?.voiceDesignTab || 'Voice 设计'}</h4>
            <p className="text-xs text-white/40">{t?.voiceDesignDesc || '描述你想要的语音，AI 将生成它。无需音频样本。'}</p>
            <label className="text-xs font-black uppercase text-white/55">{t?.voiceDesignPrompt || '语音描述'}</label>
            <textarea
              value={designPrompt}
              onChange={e => setDesignPrompt(e.target.value)}
              placeholder={t?.voiceDesignPlaceholder || '例如：温暖、温柔的女声，语调柔和...'}
              className="w-full h-20 bg-black/40 border border-white/10 rounded-2xl p-3 text-sm text-white/80 outline-none focus:border-sky-500/50 resize-none"
            />
            <label className="text-xs font-black uppercase text-white/55">{t?.voiceDesignName || '语音名称'}</label>
            <input
              value={designName}
              onChange={e => setDesignName(e.target.value)}
              placeholder="e.g. Storyteller_v1"
              className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-sm text-white/80 outline-none focus:border-sky-500/50"
            />
            <button
              onClick={handleDesign}
              disabled={designing || !designPrompt.trim() || !designName.trim()}
              className="w-full py-3 bg-sky-500/20 border border-sky-500/30 rounded-2xl text-sm font-black uppercase tracking-widest text-sky-400 hover:bg-sky-500/30 disabled:opacity-70 disabled:cursor-not-allowed transition-all relative overflow-hidden"
            >
              {designing ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
                  {t?.generating || '生成中...'}
                </span>
              ) : (
                t?.generateVoice || '生成语音'
              )}
            </button>
            {designing && (
              <div className="h-0.5 w-full bg-white/5 rounded-full overflow-hidden mt-1">
                <motion.div
                  className="h-full bg-gradient-to-r from-sky-400 to-indigo-400"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 8, ease: 'easeInOut' }}
                />
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white/[0.02] border border-white/5 p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-bold text-white/80">{t?.autoSpeakReply || '自动语音回复'}</p>
              <p className="text-[11px] text-white/45 mt-0.5">{t?.autoSpeakReplyDesc || '开启后，小枫回复时会自动朗读；关闭则仅显示文字，不发声。'}</p>
            </div>
            <button
              onClick={() => setAutoSpeakReply(!autoSpeakReply)}
              className={`w-11 h-6 rounded-full transition-all relative shrink-0 ${autoSpeakReply ? 'bg-celestial-saturn' : 'bg-white/10 border border-white/20'}`}
              aria-label={t?.autoSpeakReply || '自动语音回复'}
            >
              <span
                className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-md transition-all"
                style={{ left: autoSpeakReply ? '22px' : '2px' }}
              />
            </button>
          </div>
          <div className="rounded-2xl bg-white/[0.02] border border-white/5 p-4">
            <h4 className="text-xs font-black uppercase tracking-widest text-white/55 mb-4">{t?.voiceCloning || '语音克隆'}</h4>
            <VoiceForge t={t} compact onCloneSuccess={() => setVoiceRefresh(n => n + 1)} />
          </div>
        </div>

        <div className="overflow-y-auto scrollbar-hide rounded-2xl bg-white/[0.02] border border-white/5 p-4 space-y-6">
          {voices.cloned.length > 0 && (
            <section className="space-y-3">
              <h4 className="text-xs font-black uppercase tracking-[0.3em] text-white/40">{t?.clonedVoices || '克隆音色'}</h4>
              <div className="space-y-2">
                {voices.cloned.map((v: any) => (
                  <VoiceCard key={v.voiceId} voice={v} isClone isPlaying={playingId === v.voiceId} onPlay={() => handlePlay(v.voiceId)} />
                ))}
              </div>
            </section>
          )}
          <section className="space-y-3">
            <h4 className="text-xs font-black uppercase tracking-[0.3em] text-white/40">{t?.premadeVoices || '预设语音'}</h4>
            <div className="space-y-2">
              {voices.premade.map((v: any) => (
                <VoiceCard key={v.voiceId} voice={v} isPlaying={playingId === v.voiceId} onPlay={() => handlePlay(v.voiceId)} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
